import React, { useState, useEffect, useCallback } from 'react';
import AIQuizGenerationSlide from './carousel-slides/AIQuizGenerationSlide';
import LiveEngagementSlide from './carousel-slides/LiveEngagementSlide';
import LiveAnalyticsSlide from './carousel-slides/LiveAnalyticsSlide';
import PerformanceInsightsSlide from './carousel-slides/PerformanceInsightsSlide';
import ProblemAreasSlide from './carousel-slides/ProblemAreasSlide';

const slides = [
  { id: 'ai-generation', component: AIQuizGenerationSlide, title: 'AI Quiz Generation' },
  { id: 'engagement', component: LiveEngagementSlide, title: 'Real-Time Engagement' },
  { id: 'analytics', component: LiveAnalyticsSlide, title: 'Live Analytics' },
  { id: 'performance', component: PerformanceInsightsSlide, title: 'Performance Insights' },
  { id: 'problem-areas', component: ProblemAreasSlide, title: 'Problem Areas' }
];

const ClassroomCarousel: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % slides.length);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  }, [currentSlide, goToSlide]);

  // Auto-advance carousel every 6 seconds unless hovered
  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(nextSlide, 6000);
      return () => clearInterval(interval);
    }
  }, [isHovered, nextSlide]);

  const CurrentSlideComponent = slides[currentSlide].component;

  return (
    <div
      className="relative w-full bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Slide Container */}
      <div
        className="relative min-h-[600px] md:min-h-[700px] transition-opacity duration-500"
        style={{ opacity: isTransitioning ? 0.5 : 1 }}
      >
        <CurrentSlideComponent key={slides[currentSlide].id} />
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        disabled={isTransitioning}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed z-10"
        aria-label="Previous slide"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button
        onClick={nextSlide}
        disabled={isTransitioning}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed z-10"
        aria-label="Next slide"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            onClick={() => goToSlide(index)}
            disabled={isTransitioning}
            className={`transition-all duration-300 rounded-full ${
              index === currentSlide
                ? 'w-12 h-3 bg-orange-500'
                : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
            } disabled:cursor-not-allowed`}
            aria-label={`Go to ${slide.title}`}
            title={slide.title}
          />
        ))}
      </div>

    </div>
  );
};

export default ClassroomCarousel;
